import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ONBOARDING_STEPS, type PlanDto, type PlatformAuditDto, type PlatformOrgDetail, type ResellerRowDto, type SubscriptionDto } from "@delta/shared";
import { useLang, type T } from "../../i18n";
import { api } from "../../lib/api";
import { Icon } from "../../components/Icon";
import { Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { priceText } from "../settings/Package";
import { errText, fieldErrs, statusPill, Title, when } from "./common";
import { termsOf } from "./Packages";
import { OwnerSetupModal } from "./AddBusiness";
import { DataManagement } from "./DataManagement";

// A business in the Delta Platform portal (request of 29 Oct 2026, item 3): a summary on top (business and owner,
// status, access, package, expiry, days left), three tabs (Overview, Package & Subscription, Activity), the four
// everyday package actions each in its own dialog, and the exceptional ones under More actions. Every action keeps
// its server checks, its reason and its confirmation; nothing here reads the business's books.

const DAY = 86_400_000;
const addDays = (d: string, n: number) => new Date(Date.parse(d) + n * DAY).toISOString().slice(0, 10);
/** Days left including today, as the owner's package screen counts them. */
const daysLeft = (today: string, endsOn: string) => Math.round((Date.parse(endsOn) - Date.parse(today)) / DAY) + 1;
const currentSub = (o: PlatformOrgDetail) => o.subscriptions.find((s) => s.timing === "current") ?? null;
const scheduledSubs = (o: PlatformOrgDetail) => o.subscriptions.filter((s) => s.timing === "scheduled").sort((a, b) => a.startsOn.localeCompare(b.startsOn));

type Tab = "overview" | "package" | "activity" | "data";
type SubAct = "trial" | "change" | "renew" | "grant";
type Open = SubAct | "extend" | "payment" | "cancel" | "rules" | "suspend" | "reactivate" | "recovery" | "reseller";
type Done = (o: PlatformOrgDetail) => void;

export function BusinessDetail() {
  const { t, L } = useLang();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (["overview", "package", "activity", "data"].includes(params.get("tab") ?? "") ? params.get("tab") : "overview") as Tab;
  const [o, setO] = useState<PlatformOrgDetail | null>(null);
  const [plans, setPlans] = useState<PlanDto[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const [open, setOpen] = useState<Open | null>(null);
  const load = useCallback(() => api<PlatformOrgDetail>("GET", `/platform/organizations/${id}`).then(setO, (e) => setFailed(errText(t, e))), [id, t]);
  useEffect(() => { void load(); api<PlanDto[]>("GET", "/platform/plans").then(setPlans, () => undefined); }, [load]);
  if (failed) return <div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div>;
  if (!o) return <div className="state-box"><p>{t("pfLoading")}</p></div>;
  const done: Done = (x) => { setO(x); setOpen(null); };
  const pick = (k: Tab) => { const p = new URLSearchParams(params); if (k === "overview") p.delete("tab"); else p.set("tab", k); setParams(p, { replace: true }); };
  return (
    <>
      <Link className="pf-small" to="/platform/businesses">{t("pfNavBusinesses")}</Link>
      <Summary o={o} />
      <Actions o={o} onOpen={setOpen} />
      <div className="tabs pf-tabs" role="tablist" aria-label={L(o)}>
        {(["overview", "package", "activity", "data"] as const).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tab ${tab === k ? "on" : ""}`} data-testid={`pf-tab-${k}`} onClick={() => pick(k)}>
            {t(`bdTab_${k}`)}{k === "package" && o.requests.some((r) => r.status === "open") && <span className="count">{o.requests.filter((r) => r.status === "open").length}</span>}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview o={o} plans={plans} onDone={setO} />}
      {tab === "package" && <PackageTab o={o} onDone={setO} />}
      {tab === "data" && <DataManagement o={o} />}
      {tab === "activity" && <section className="card list-card" data-testid="pf-history"><div className="card-head"><h2>{t("bdTab_activity")}</h2></div><ActivityList rows={o.history} plans={plans} /></section>}
      {open && ["trial", "change", "renew", "grant"].includes(open) && <SubActionModal o={o} plans={plans} act={open as SubAct} onClose={() => setOpen(null)} onDone={done} />}
      {open === "extend" && <ExtendModal o={o} onClose={() => setOpen(null)} onDone={done} />}
      {open === "payment" && <PaymentModal o={o} onClose={() => setOpen(null)} onDone={done} />}
      {open === "cancel" && <CancelModal o={o} onClose={() => setOpen(null)} onDone={done} />}
      {open === "rules" && <RulesModal o={o} onClose={() => setOpen(null)} onDone={done} />}
      {(open === "suspend" || open === "reactivate") && <StatusModal o={o} kind={open} onClose={() => setOpen(null)} onDone={done} />}
      {open === "recovery" && <RecoveryModal o={o} onClose={() => setOpen(null)} onDone={done} />}
      {open === "reseller" && <ResellerModal o={o} onClose={() => setOpen(null)} onDone={done} />}
    </>
  );
}

// ---------------------------------------------------------------- summary and actions

function Summary({ o }: { o: PlatformOrgDetail }) {
  const { t, L, lang } = useLang();
  const cur = currentSub(o);
  const next = scheduledSubs(o)[0];
  const owner = o.signupDetail ? { name: o.signupDetail.ownerName, mobile: o.signupDetail.mobile } : o.administrators[0] ?? null;
  const left = cur ? daysLeft(o.today, cur.endsOn) : null;
  const signup = o.origin === "signup";
  const state = o.packageState;
  return (
    <section className="card pf-summary" data-testid="pf-summary">
      <div className="pf-sum-head">
        <div><Title text={L(o)} />
          <div className="pf-small">{owner ? <>{t("bdOwner")}: <b>{owner.name}</b> · <span className="num" dir="ltr">{owner.mobile}</span> · </> : null}{t(`pfOrigin_${o.origin}`)} · {t("pfCreatedAt")} {when(o.createdAt, lang)}
            {o.reseller && <> · {t("bdReseller")}: <Link to={`/platform/resellers/${o.reseller.id}`} data-testid="pf-reseller">{L(o.reseller)}</Link></>}</div></div>
      </div>
      <dl className="pf-sum-grid">
        <div><dt>{t("bdStatus")}</dt><dd>{statusPill(t, o.status)}</dd></div>
        <div><dt>{t("bdAccess")}</dt><dd data-testid="pf-access">
          {o.status !== "active" ? <span className="pill bad">{t(`bdClosedStatus_${o.status}`)}</span>
            : o.access && o.access !== "open" ? <span className="pill bad" data-testid="pf-closed">{t(`pfClosed_${o.access}`)}</span>
            : <span className="pill good">{t("bdOpen")}</span>}
          {signup && <div className="pf-small" data-testid="pf-rules" data-rules={o.packageRules}>{t(`bdRules_${o.packageRules ?? "enforced"}`)}</div>}
          {!signup && <div className="pf-small">{t("bdProvisioned")}</div>}</dd></div>
        <div><dt>{t("bdPackage")}</dt><dd>{cur ? <b>{L(cur.plan)}</b> : null}
          {state && <div><span className={`pill ${state === "paid" ? "good" : state === "trial" ? "info" : state === "expired" ? "bad" : ""}`} data-testid="pf-package-state">{t(`pfPkg_${state}`)}</span></div>}</dd></div>
        <div><dt>{t("bdExpiry")}</dt><dd className="num" data-testid="pf-expiry">{cur ? cur.endsOn : "--"}
          {next && <div className="pf-small">{t("bdThen", { x: L(next.plan), d: next.startsOn })}</div>}</dd></div>
        <div><dt>{t("bdDaysLeft")}</dt><dd data-testid="pf-days-left" className={`num ${left !== null && left <= 7 ? "bad-text" : ""}`}>{left === null ? "--" : left === 1 ? t("bdLastDay") : left}</dd></div>
      </dl>
      {o.statusReason && o.status !== "active" && <div className="pf-small">{t("pfStatusReason")}: {o.statusReason}</div>}
    </section>
  );
}

function Actions({ o, onOpen }: { o: PlatformOrgDetail; onOpen(k: Open): void }) {
  const { t } = useLang();
  const [menu, setMenu] = useState(false);
  useEffect(() => {
    if (!menu) return;
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") setMenu(false); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [menu]);
  const cur = currentSub(o);
  const live = o.subscriptions.filter((s) => s.status === "active" && (s.timing === "current" || s.timing === "scheduled"));
  const usable = o.status !== "rejected" && o.status !== "pending";
  const main: [Open, string, boolean][] = [
    ["trial", "pf-act-trial", usable && o.origin === "signup" && !o.trialUsed],
    ["change", "pf-act-change", usable],
    ["renew", "pf-act-renew", usable && o.subscriptions.some((s) => s.status === "active")],
    ["extend", "pf-extend", usable && cur?.kind === "trial"]
  ];
  const more: [Open, string, boolean][] = [
    ["grant", "pf-act-grant", usable],
    ["payment", "pf-more-payment", live.some((s) => s.kind === "paid" && s.payment.kind === "none")],
    ["cancel", "pf-more-cancel", live.length > 0],
    ["rules", "pf-more-rules", o.origin === "signup" && o.packageRules === "legacy" && o.status !== "rejected"],
    ["suspend", "pf-more-suspend", o.status === "active" || o.status === "expired"],
    ["reactivate", "pf-more-reactivate", o.status === "suspended"],
    ["recovery", "pf-more-recovery", o.status !== "rejected" && o.administrators.length > 0],
    ["reseller", "pf-more-reseller", o.origin === "signup" && o.status !== "pending" && o.status !== "rejected"]
  ];
  const shown = more.filter(([, , on]) => on);
  return (
    <div className="pf-row pf-head-actions" data-testid="pf-actions-bar">
      {main.filter(([, , on]) => on).map(([k, id]) => (
        <button key={k} type="button" className={`btn ${k === "change" ? "btn-primary" : ""}`} data-testid={id} onClick={() => onOpen(k)}>{t(`bdAct_${k}`)}</button>
      ))}
      {shown.length > 0 && <div className="pf-more">
        <button type="button" className="btn btn-ghost" aria-haspopup="menu" aria-expanded={menu} data-testid="pf-more" onClick={() => setMenu(!menu)}>{t("bdMore")} <Icon name="chev" /></button>
        <div className={`menu-pop ${menu ? "open" : ""}`} role="menu" data-testid="pf-more-menu">
          {shown.map(([k, id]) => <button key={k} type="button" role="menuitem" data-testid={id} className={k === "suspend" || k === "cancel" ? "bad-text" : ""} onClick={() => { setMenu(false); onOpen(k); }}>{t(`bdMore_${k}`)}</button>)}
        </div>
      </div>}
    </div>
  );
}

// ---------------------------------------------------------------- overview

function ReasonAction({ label, hint, danger, min = 5, field = "reason", onRun, testId }: { label: string; hint: string; danger?: boolean; min?: number; field?: string; onRun(text: string): Promise<void>; testId: string }) {
  const { t } = useLang();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="pf-actions" data-testid={testId}>
      <span className="pf-small">{hint}</span>
      <textarea className="control" rows={2} aria-label={field === "note" ? t("pfAttestation") : t("pfReason")} placeholder={field === "note" ? t("pfAttestation") : t("pfReason")} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="pf-row"><button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} disabled={busy || text.trim().length < min} onClick={async () => { setBusy(true); try { await onRun(text.trim()); setText(""); } finally { setBusy(false); } }}>{label}</button></div>
    </div>
  );
}

function Overview({ o, plans, onDone }: { o: PlatformOrgDetail; plans: PlanDto[]; onDone: Done }) {
  const { t, L, lang } = useLang();
  const toast = useToast();
  const [startTrial, setStartTrial] = useState(false);
  // Migration 044: a new business is closed until its trial starts; the activating Super Admin may start one now.
  const [trialPlanId, setTrialPlanId] = useState("");
  const act = async (path: string, body: unknown) => {
    try { onDone(await api<PlatformOrgDetail>("POST", path, body)); toast(t("pfSaved")); } catch (e) { toast(errText(t, e)); }
  };
  const s = o.signupDetail;
  const ver = (st: string) => <span className={`pill ${st === "verified" ? "good" : st === "blocked" ? "bad" : "warn"}`}>{t(`pfVer_${st}`)}</span>;
  const pending = o.status === "pending" && s && (s.status === "pending_verification" || s.status === "pending_review");
  const verified = s && s.mobileVerifiedAt && s.emailVerifiedAt;
  // The trial offer recorded at signup starts at activation only when the Super Admin ticks it (migration 043).
  const trial = o.offer && !o.trialUsed && startTrial ? { startTrial: true } : !o.trialUsed && trialPlanId ? { trialPlanId } : {};
  return (
    <div className="pf-grid">
      {s && (
        <section className="card" data-testid="pf-signup">
          <div className="card-head"><h2>{t("pfSignupReview")}</h2><span className="pill info">{t(`pfSignup_${s.status}`)}</span></div>
          <div className="pf-card-b">
            <dl className="pf-kv">
              <dt>{t("pfOwner")}</dt><dd>{s.ownerName}</dd>
              <dt>{t("pfMobile")}</dt><dd className="num" dir="ltr">{s.mobile} {ver(s.verification.sms)}</dd>
              <dt>{t("pfEmailF")}</dt><dd dir="ltr">{s.email ?? "--"} {s.email && ver(s.verification.email)}</dd>
              <dt>{t("pfLanguage")}</dt><dd>{s.language === "ar" ? "العربية" : "English"}</dd>
              <dt>{t("pfReviewMode")}</dt><dd><span className={`pill ${s.reviewMode === "verified" ? "good" : s.reviewMode === "platform" ? "info" : "warn"}`} data-testid="pf-review-mode">{t(`pfReviewMode_${s.reviewMode}`)}</span></dd>
              {s.createdBy && <><dt>{t("abCreatedBy")}</dt><dd>{s.createdBy} · {when(s.createdAt, lang)}</dd></>}
              {s.activatedAt && <><dt>{t("pfActivate")}</dt><dd>{t("pfActivatedBy", { a: s.activatedBy ?? "", d: when(s.activatedAt, lang) })} · {t(`pfMethod_${s.activationMethod}`)}{s.attestationNote && <div className="pf-small">{s.attestationNote}</div>}</dd></>}
              {s.rejectedAt && <><dt>{t("pfReject")}</dt><dd>{when(s.rejectedAt, lang)} · {s.rejectReason}</dd></>}
            </dl>
            {pending && !(o.offer && startTrial) && <div className="pf-row" data-testid="pf-activate-trial"><label className="pf-small" htmlFor="pf-at">{t("pfActivateTrial")}</label>
              <select id="pf-at" className="control" style={{ maxWidth: 280 }} value={trialPlanId} onChange={(e) => setTrialPlanId(e.target.value)}>
                <option value="">{t("pfActivateNoTrial")}</option>
                {plans.filter((p) => p.active && p.kind === "trial").map((p) => <option key={p.id} value={p.id}>{L(p)} · {t("pkgTrialDays", { n: p.trialDays ?? 0 })}</option>)}
              </select><span className="pf-small">{t("pfActivateTrialHint")}</span></div>}
            {pending && o.offer && !o.trialUsed && <label className="check" data-testid="pf-start-trial"><input type="checkbox" checked={startTrial} onChange={(e) => setStartTrial(e.target.checked)} /> {t("pfStartTrialOnActivate", { x: L(o.offer), n: o.offer.trialDays })}</label>}
            {pending && (verified
              ? <div className="pf-actions"><div className="pf-row"><button className="btn btn-primary" data-testid="pf-activate-verified" onClick={() => void act(`/platform/organizations/${o.id}/activate`, { method: "verified", ...trial })}>{t("pfActivateVerified")}</button></div></div>
              : s.reviewMode === "platform"
                ? <ReasonAction testId="pf-activate-platform" field="note" min={10} label={t("pfActivatePlatform")} hint={t("pfActivatePlatformHint")} onRun={(n) => act(`/platform/organizations/${o.id}/activate`, { method: "platform_created", note: n, ...trial })} />
              : s.reviewMode === "manual_review"
                ? <ReasonAction testId="pf-activate-manual" field="note" min={10} label={t("pfActivateManual")} hint={t("pfActivateManualHint")} onRun={(n) => act(`/platform/organizations/${o.id}/activate`, { method: "manual_attestation", note: n, ...trial })} />
                : <p className="pf-small" data-testid="pf-await-verification">{t("pfAwaitVerification")}</p>)}
            {pending && <ReasonAction testId="pf-reject" label={t("pfReject")} hint={t("pfRejectHint")} danger onRun={(r) => act(`/platform/organizations/${o.id}/reject`, { reason: r })} />}
          </div>
        </section>
      )}
      <section className="card" data-testid="pf-overview">
        <div className="card-head"><h2>{t("bdUsage")}</h2></div>
        <div className="pf-card-b">
          <div className="pf-usage" data-testid="pf-usage">
            {([["pfUsers", o.usage.users], ["pfBranches", o.usage.branches], ["pfWarehouses", o.usage.warehouses], ["pfTreasuries", o.usage.treasuries], ["pfPosDevices", o.usage.posDevices]] as const).map(([k, n]) => (
              <div key={k}><b className="num">{n}</b><span>{t(k)}</span></div>
            ))}
          </div>
          <dl className="pf-kv">
            <dt>{t("pfLastSignIn")}</dt><dd className="num">{when(o.usage.lastSignInAt, lang)}</dd>
            {!s && o.administrators[0] && <><dt>{t("pfOwner")}</dt><dd>{o.administrators[0].name} · <span className="num" dir="ltr">{o.administrators[0].mobile}</span></dd></>}
            {o.onboardingSteps && <><dt>{t("pfOnboardingSteps")}</dt><dd><div className="pf-steps">{ONBOARDING_STEPS.map((k) => (
              <span key={k} className={`pill ${o.onboardingSteps![k] === "done" ? "good" : o.onboardingSteps![k] === "later" ? "warn" : ""}`}>{t(`obStep_${k}`)}</span>
            ))}</div></dd></>}
          </dl>
        </div>
      </section>
      {s?.reviewMode === "platform" && <OwnerSetupCard o={o} />}
      {o.recoveries.length > 0 && (
        <section className="card" data-testid="pf-recovery">
          <div className="card-head"><h2>{t("pfRecovery")}</h2></div>
          <table className="tbl"><tbody>{o.recoveries.map((r) => (
            <tr key={r.id}><td>{r.userName}<div className="pf-small">{r.reason}</div></td><td>{t(`pfChannel_${r.channel}`)}</td>
              <td><span className={`pill ${r.status === "completed" ? "good" : r.status === "blocked" ? "bad" : "warn"}`} data-rec={r.status}>{t(`pfRec_${r.status}`)}</span></td><td className="num">{when(r.requestedAt, lang)}</td></tr>
          ))}</tbody></table>
        </section>
      )}
    </div>
  );
}

/** The owner's setup codes of a business added by a Super Admin (migration 053), and a new one while unused. */
function OwnerSetupCard({ o }: { o: PlatformOrgDetail }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(o.ownerSetups ?? []);
  useEffect(() => setList(o.ownerSetups ?? []), [o]);
  const used = list.some((x) => x.status === "used");
  const reload = () => void api<PlatformOrgDetail>("GET", `/platform/organizations/${o.id}`).then((x) => setList(x.ownerSetups ?? []), () => undefined);
  return (
    <section className="card" data-testid="pf-owner-setup">
      <div className="card-head"><h2>{t("abOwnerSetup")}</h2>
        {!used && o.status !== "rejected" && <button type="button" className="btn btn-sm" data-testid="pf-owner-setup-new" onClick={() => setOpen(true)}>{t("abNewCode")}</button>}</div>
      <table className="tbl"><tbody>{list.map((x) => (
        <tr key={x.id}><td>{t(`abDelivery_${x.delivery}`)}{x.destination && <div className="pf-small num" dir="ltr">{x.destination}</div>}</td>
          <td><span className={`pill ${x.status === "used" ? "good" : x.status === "open" ? "warn" : ""}`} data-setup={x.status}>{t(`abSetupStatus_${x.status}`)}</span></td>
          <td className="num">{when(x.createdAt, lang)}<div className="pf-small">{x.createdBy}</div></td>
          <td className="num pf-small">{x.consumedAt ? when(x.consumedAt, lang) : `→ ${when(x.expiresAt, lang)}`}</td></tr>
      ))}</tbody></table>
      {open && <OwnerSetupModal o={o} onClose={() => setOpen(false)} onDone={reload} />}
    </section>
  );
}

// ---------------------------------------------------------------- package & subscription

function PackageTab({ o, onDone }: { o: PlatformOrgDetail; onDone: Done }) {
  const { t, L, lang } = useLang();
  const toast = useToast();
  const signup = o.origin === "signup";
  const e = o.entitlements;
  const resolve = async (id: string, status: "done" | "declined", response: string) => {
    try { onDone(await api("POST", `/platform/subscription-requests/${id}/resolve`, { status, response })); toast(t("pfSaved")); } catch (err) { toast(errText(t, err)); }
  };
  const timingPill = (s: SubscriptionDto) => <span className={`pill ${s.timing === "current" ? "good" : s.timing === "scheduled" ? "info" : ""}`} data-timing={s.timing}>{t(`pfTiming_${s.timing}`)}</span>;
  return (
    <section className="card pf-subs" data-testid="pf-subscriptions">
      <div className="card-head"><h2>{t("bdTab_package")}</h2></div>
      <div className="pf-card-b">
        <dl className="pf-kv">
          <dt>{t("pfEntitlements")}</dt>
          <dd>{e ? <>{t(`pfSource_${e.source}`)}{e.plan && ` · ${L(e.plan)} v${e.plan.version ?? 1}`}{e.plan && <span className="num"> · {e.plan.startsOn} → {e.plan.endsOn}</span>}
            <div className="pf-small num">{t("obLimits", { u: e.limits.users, b: e.limits.branches, w: e.limits.warehouses, t: e.limits.treasuries, p: e.limits.posDevices })}</div>
            <div className="pf-small">{t("pkgModules")}: {(e.features ?? []).map((f) => t(`pkgFeature_${f}`)).join(" · ")}</div></> : t("pfProvisionedLimits")}</dd>
          {signup && <><dt>{t("pfRules")}</dt><dd>{t(`pfRules_${o.packageRules ?? "enforced"}`)}</dd></>}
          {signup && <><dt>{t("pfTrial")}</dt><dd data-testid="pf-trial-used">{o.trialUsed ? t("pfTrialUsed") : t("pfTrialNotUsed")}</dd></>}
          {o.offer && <><dt>{t("pfSignupOffer")}</dt><dd>{L(o.offer)} · {t("pkgTrialDays", { n: o.offer.trialDays })}</dd></>}
          <dt>{t("pfPayment")}</dt><dd className="pf-small" data-testid="pf-payment-note">{t("bdPaymentNote")}</dd>
        </dl>
        {o.packageRules === "legacy" && signup && o.packageState === "trial" && <div className="pkg-notice bad" role="status" data-testid="pf-legacy-trial"><Icon name="alert" /><span>{t("pfLegacyTrialWarn")}</span></div>}
        {o.subscriptions.length > 0 ? (
          <div className="pf-scroll"><table className="tbl" data-testid="pf-sub-table"><thead><tr><th>{t("pkgName")}</th><th>{t("pkgDates")}</th><th>{t("pfTimingCol")}</th><th>{t("pkgPayment")}</th></tr></thead>
            <tbody>{o.subscriptions.map((x) => (
              <tr key={x.id} data-sub={x.id} data-kind={x.kind}>
                <td>{L(x.plan)} <span className="pf-small">v{x.version} · {t(`pkgKind_${x.kind}`)}</span><div className="pf-small">{t(`pkgChange_${x.changeKind}`)} · {t("pfGrantedBy", { a: x.grantedBy })} · {x.reason}</div>
                  {x.cancelReason && <div className="pf-small">{t("pfCancelled")}: {x.cancelReason}</div>}</td>
                <td className="num">{x.startsOn} → {x.endsOn}{x.originalEndsOn && <div className="pf-small">{t("pkgExtendedFrom", { d: x.originalEndsOn })}</div>}</td>
                <td>{timingPill(x)}</td>
                <td>{x.payment.kind === "recorded_manual" ? <span title={`${x.payment.recordedBy ?? ""} ${when(x.payment.recordedAt, lang)}`}><span className="pill">{t("pkgPaymentManual")}</span><div className="pf-small num">{x.payment.reference}{x.payment.amount && ` · ${x.payment.amount} ${x.payment.currency ?? ""}`}</div></span>
                  : <span className="pf-small">{x.kind === "trial" ? "--" : t("pkgPaymentNone")}</span>}</td>
              </tr>
            ))}</tbody></table></div>
        ) : <p className="pf-small">{t("bdNoSubs")}</p>}
        {o.requests.length > 0 && <Requests o={o} onResolve={resolve} />}
      </div>
    </section>
  );
}

function Requests({ o, onResolve }: { o: PlatformOrgDetail; onResolve(id: string, s: "done" | "declined", r: string): Promise<void> }) {
  const { t, L, lang } = useLang();
  const [resp, setResp] = useState<Record<string, string>>({});
  return (
    <div className="pf-reqs" data-testid="pf-requests"><b>{t("pfRequests")}</b>
      <table className="tbl"><tbody>{o.requests.map((r) => (
        <tr key={r.id} data-req={r.status}>
          <td>{t(`pkgReq_${r.kind}`)}{r.plan && ` · ${L(r.plan)}`}<div className="pf-small">{r.requestedBy} · {when(r.createdAt, lang)}</div>{r.note && <div className="pf-small">{r.note}</div>}</td>
          <td><span className={`pill ${r.status === "open" ? "warn" : r.status === "done" ? "good" : ""}`}>{t(`pkgReqStatus_${r.status}`)}</span>{r.response && <div className="pf-small">{r.response}</div>}</td>
          <td>{r.status === "open" && <div className="pf-row">
            <input className="control" style={{ maxWidth: 220 }} placeholder={t("pfResponse")} aria-label={t("pfResponse")} value={resp[r.id] ?? ""} onChange={(e) => setResp({ ...resp, [r.id]: e.target.value })} />
            <button className="btn btn-sm" disabled={(resp[r.id] ?? "").trim().length < 2} onClick={() => void onResolve(r.id, "done", resp[r.id]!)}>{t("pfReqDone")}</button>
            <button className="btn btn-sm" disabled={(resp[r.id] ?? "").trim().length < 2} onClick={() => void onResolve(r.id, "declined", resp[r.id]!)}>{t("pfReqDecline")}</button></div>}</td>
        </tr>
      ))}</tbody></table>
    </div>
  );
}

// ---------------------------------------------------------------- dialogs

function Reason({ v, set }: { v: string; set(v: string): void }) {
  const { t } = useLang();
  return <Field label={t("pfReason")} req hint={t("bdReasonHint")}><textarea className="control" rows={2} aria-label={t("pfReason")} value={v} onChange={(e) => set(e.target.value)} /></Field>;
}

function PaymentFields({ v, set }: { v: { ref: string; amount: string; currency: string }; set(p: Partial<{ ref: string; amount: string; currency: string }>): void }) {
  const { t } = useLang();
  return (
    <fieldset className="pf-pay"><legend>{t("pfPayRecord")}</legend>
      <span className="pf-small">{t("pfPayRecordHint")}</span>
      <div className="pf-row">
        <input className="control" style={{ maxWidth: 220 }} placeholder={t("pfPayRef")} aria-label={t("pfPayRef")} value={v.ref} onChange={(e) => set({ ref: e.target.value })} />
        <input className="control num" style={{ maxWidth: 120 }} placeholder={t("pfPayAmount")} aria-label={t("pfPayAmount")} inputMode="decimal" value={v.amount} onChange={(e) => set({ amount: e.target.value })} />
        <input className="control num" style={{ maxWidth: 70 }} aria-label={t("pfCurrency")} maxLength={3} value={v.currency} onChange={(e) => set({ currency: e.target.value.toUpperCase() })} />
      </div>
    </fieldset>
  );
}

/** Runs a dialog's request: the result replaces the business, a refusal stays in the dialog with its field errors. */
function useRun(onDone: Done) {
  const { t } = useLang();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const run = async (path: string, body: unknown) => {
    setBusy(true);
    try { onDone(await api<PlatformOrgDetail>("POST", path, body)); toast(t("pfSaved")); }
    catch (e) { setErrs(fieldErrs(t, e)); setAlert(errText(t, e)); }
    finally { setBusy(false); }
  };
  return { busy, alert, errs, run };
}

function SubActionModal({ o, plans, act, onClose, onDone }: { o: PlatformOrgDetail; plans: PlanDto[]; act: SubAct; onClose(): void; onDone: Done }) {
  const { t, L } = useLang();
  const signup = o.origin === "signup";
  const legacy = signup && o.packageRules === "legacy";
  const list = plans.filter((p) => p.active && p.kind === (act === "trial" ? "trial" : "paid"));
  const [v, setV] = useState({ planId: act === "renew" ? "" : list[0]?.id ?? "", startsOn: o.today, endsOn: "", reason: "", pay: false, ref: "", amount: "", currency: "SAR", toRules: false });
  // Generated once per dialog: a retry or a second click is answered with the first result.
  const [requestId] = useState(() => crypto.randomUUID());
  const { busy, alert, errs, run } = useRun(onDone);
  const planId = v.planId || (act === "renew" ? "" : list[0]?.id || "");
  const submit = () => {
    const payment = v.pay && v.ref.trim() ? { reference: v.ref.trim(), amount: v.amount || null, currency: v.currency || null } : null;
    const [path, body] = act === "trial" ? ["trial", { planId, startsOn: v.startsOn, reason: v.reason, requestId, ...(legacy ? { applyPackageRules: v.toRules } : {}) }]
      : act === "grant" ? ["subscriptions", { planId, startsOn: v.startsOn, endsOn: v.endsOn, reason: v.reason, requestId, payment }]
      : act === "change" ? ["change", { planId, effectiveOn: v.startsOn, endsOn: v.endsOn, reason: v.reason, requestId, payment }]
      : ["renew", { ...(planId ? { planId } : {}), endsOn: v.endsOn, reason: v.reason, requestId, payment }];
    void run(`/platform/organizations/${o.id}/${path}`, body);
  };
  // A trial for a business on the earlier rules starts only with an explicit move to package rules (else it never ends).
  const blocked = act === "trial" && (o.trialUsed || o.status !== "active" || (legacy && !v.toRules));
  const trialDays = list.find((p) => p.id === planId)?.trialDays ?? 1;
  const cur = currentSub(o);
  return (
    <Modal title={t(`bdAct_${act}`)} sub={cur ? t("bdNow", { x: L(cur.plan), d: cur.endsOn }) : t("bdNowNone")} onClose={onClose} onSubmit={submit} alert={alert} busy={busy} saveLabel={t(`bdAct_${act}`)} saveTestId="pf-act-submit"
      saveDisabled={blocked || v.reason.trim().length < 5 || (act !== "trial" && !v.endsOn) || (act !== "renew" && !planId)}>
      <div className="pf-dialog" data-testid="pf-sub-actions">
        <p className="pf-small">{t(`pfActHint_${act}`)}</p>
        {signup && <div className={`pkg-notice ${legacy ? "warn" : "info"}`} role="note" data-testid="pf-act-policy" data-rules={o.packageRules}><Icon name="info" />
          <span>{t(legacy ? (act === "trial" ? "pfPolicy_legacyTrial" : "pfPolicy_legacy") : "pfPolicy_enforced")}</span></div>}
        {act === "trial" && o.trialUsed && <span className="pill bad" data-testid="pf-trial-blocked">{t("pfErr_trial_already_used")}</span>}
        {act === "trial" && legacy && !o.trialUsed && <label className="check" data-testid="pf-trial-to-rules"><input type="checkbox" checked={v.toRules} onChange={(e) => setV({ ...v, toRules: e.target.checked })} /> <span className="bad-text">{t("pfTrialToRules")}</span></label>}
        {act === "trial" && !o.trialUsed && o.status !== "active" && <span className="pill warn">{t("pfTrialNeedsActive")}</span>}
        {!list.length && act !== "renew" ? <p className="pf-small">{t(act === "trial" ? "pfNoTrialPlans" : "pfNoPlans")} <Link to="/platform/plans">{t("pfNewPlan")}</Link></p> : (<>
          <Field label={t("pfPlan")} req={act !== "renew"} error={errs.planId}>
            <select className="control" aria-label={t("pfPlan")} data-testid="pf-act-plan" value={planId} onChange={(e) => setV({ ...v, planId: e.target.value })}>
              {act === "renew" && <option value="">{t("pfRenewSame")}</option>}
              {list.map((p) => <option key={p.id} value={p.id}>{L(p)} ({p.code}) · {priceText(t, termsOf(p))}</option>)}</select>
          </Field>
          <div className="pf-dates">
            {act !== "renew" && <Field label={t(act === "change" ? "pfEffective" : "pfStarts")} req error={errs.startsOn || errs.effectiveOn}>
              <input className="control num" type="date" min={act === "grant" ? undefined : o.today} value={v.startsOn} onChange={(e) => setV({ ...v, startsOn: e.target.value })} /></Field>}
            {act !== "trial" && <Field label={t("pfEnds")} req error={errs.endsOn}><input className="control num" type="date" value={v.endsOn} onChange={(e) => setV({ ...v, endsOn: e.target.value })} /></Field>}
          </div>
          {act === "trial" && planId && <span className="pf-small num" data-testid="pf-trial-dates">{t("pfTrialDates", { s: v.startsOn, e: addDays(v.startsOn, trialDays - 1) })}</span>}
          {act !== "trial" && <label className="check"><input type="checkbox" checked={v.pay} onChange={(e) => setV({ ...v, pay: e.target.checked })} /> {t("pfPayRecordAlso")}</label>}
          {act !== "trial" && v.pay && <PaymentFields v={v} set={(p) => setV({ ...v, ...p })} />}
          <Reason v={v.reason} set={(reason) => setV({ ...v, reason })} />
        </>)}
      </div>
    </Modal>
  );
}

function ExtendModal({ o, onClose, onDone }: { o: PlatformOrgDetail; onClose(): void; onDone: Done }) {
  const { t, L } = useLang();
  const s = currentSub(o)!;
  const [v, setV] = useState({ endsOn: s.endsOn, reason: "" });
  const { busy, alert, errs, run } = useRun(onDone);
  return (
    <Modal title={t("bdAct_extend")} sub={`${L(s.plan)} · ${s.startsOn} → ${s.endsOn}`} onClose={onClose} busy={busy} alert={alert} saveLabel={t("pfExtend")}
      saveDisabled={v.reason.trim().length < 5 || v.endsOn <= s.endsOn} onSubmit={() => void run(`/platform/subscriptions/${s.id}/extend`, v)}>
      <div className="pf-dialog">
        <Field label={t("pfNewEnd")} req error={errs.endsOn} hint={t("pfExtendHint", { d: s.originalEndsOn ?? s.endsOn })}>
          <input className="control num" type="date" min={addDays(s.endsOn, 1)} value={v.endsOn} onChange={(e) => setV({ ...v, endsOn: e.target.value })} /></Field>
        <Reason v={v.reason} set={(reason) => setV({ ...v, reason })} />
      </div>
    </Modal>
  );
}

/** A choice of subscription, shown only when there is more than one to choose from. */
function SubChoice({ subs, value, set }: { subs: SubscriptionDto[]; value: string; set(id: string): void }) {
  const { t, L } = useLang();
  if (subs.length < 2) return null;
  return (
    <Field label={t("bdWhichSub")} req>
      <select className="control" data-testid="pf-which-sub" value={value} onChange={(e) => set(e.target.value)}>
        {subs.map((s) => <option key={s.id} value={s.id}>{L(s.plan)} · {s.startsOn} → {s.endsOn} · {t(`pfTiming_${s.timing}`)}</option>)}</select>
    </Field>
  );
}

function PaymentModal({ o, onClose, onDone }: { o: PlatformOrgDetail; onClose(): void; onDone: Done }) {
  const { t } = useLang();
  const subs = o.subscriptions.filter((s) => s.status === "active" && s.kind === "paid" && s.payment.kind === "none" && s.timing !== "ended");
  const [id, setId] = useState(subs[0]?.id ?? "");
  const [v, setV] = useState({ ref: "", amount: "", currency: subs[0]?.terms.currency ?? "SAR", reason: "" });
  const { busy, alert, run } = useRun(onDone);
  return (
    <Modal title={t("bdMore_payment")} sub={t("bdPaymentManual")} onClose={onClose} busy={busy} alert={alert} saveLabel={t("pfPayRecordShort")} testId="pf-payment-dialog"
      saveDisabled={!id || !v.ref.trim() || v.reason.trim().length < 5}
      onSubmit={() => void run(`/platform/subscriptions/${id}/payment`, { reference: v.ref.trim(), amount: v.amount || null, currency: v.currency || null, reason: v.reason })}>
      <div className="pf-dialog">
        <SubChoice subs={subs} value={id} set={setId} />
        <PaymentFields v={v} set={(p) => setV({ ...v, ...p })} />
        <Reason v={v.reason} set={(reason) => setV({ ...v, reason })} />
      </div>
    </Modal>
  );
}

function CancelModal({ o, onClose, onDone }: { o: PlatformOrgDetail; onClose(): void; onDone: Done }) {
  const { t } = useLang();
  const subs = o.subscriptions.filter((s) => s.status === "active" && (s.timing === "current" || s.timing === "scheduled"));
  const [id, setId] = useState(subs[0]?.id ?? "");
  const [reason, setReason] = useState("");
  const [sure, setSure] = useState(false);
  const { busy, alert, run } = useRun(onDone);
  const chosen = subs.find((s) => s.id === id);
  // Cancelling the period in force under package rules closes the business now: confirmed explicitly.
  const closes = chosen?.timing === "current" && o.origin === "signup" && o.packageRules !== "legacy";
  return (
    <Modal title={t("bdMore_cancel")} onClose={onClose} busy={busy} alert={alert} danger saveLabel={t("bdCancelSub")} testId="pf-cancel-dialog"
      saveDisabled={!id || reason.trim().length < 5 || (closes && !sure)} onSubmit={() => void run(`/platform/subscriptions/${id}/cancel`, { reason })}>
      <div className="pf-dialog">
        <SubChoice subs={subs} value={id} set={(x) => { setId(x); setSure(false); }} />
        <p className="pf-small">{t("pfCancelHint")}</p>
        {closes && <label className="check"><input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} /> <span className="bad-text">{t("bdCancelConfirm")}</span></label>}
        <Reason v={reason} set={setReason} />
      </div>
    </Modal>
  );
}

/** Moves a business that existed before migration 044 to package rules: explicit, with a reason; confirmed if it closes it now. */
function RulesModal({ o, onClose, onDone }: { o: PlatformOrgDetail; onClose(): void; onDone: Done }) {
  const { t } = useLang();
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const covered = o.subscriptions.some((s) => s.timing === "current");
  const { busy, alert, run } = useRun(onDone);
  return (
    <Modal title={t("pfMoveRules")} onClose={onClose} busy={busy} alert={alert} danger saveLabel={t("pfMoveRules")} testId="pf-move-rules"
      saveDisabled={reason.trim().length < 5 || (!covered && !confirm)} onSubmit={() => void run(`/platform/organizations/${o.id}/package-rules`, { reason, confirmBlock: confirm })}>
      <div className="pf-dialog">
        <p className="pf-small">{t("pfMoveRulesHint")}</p>
        {!covered && <label className="check"><input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} /> <span className="bad-text">{t("pfMoveRulesConfirm")}</span></label>}
        <Reason v={reason} set={setReason} />
      </div>
    </Modal>
  );
}

function StatusModal({ o, kind, onClose, onDone }: { o: PlatformOrgDetail; kind: "suspend" | "reactivate"; onClose(): void; onDone: Done }) {
  const { t } = useLang();
  const [reason, setReason] = useState("");
  const [sure, setSure] = useState(false);
  const { busy, alert, run } = useRun(onDone);
  const suspend = kind === "suspend";
  return (
    <Modal title={t(`bdMore_${kind}`)} onClose={onClose} busy={busy} alert={alert} danger={suspend} saveLabel={t(suspend ? "pfSuspend" : "pfReactivate")} testId={`pf-${kind}`}
      saveDisabled={reason.trim().length < 5 || (suspend && !sure)} onSubmit={() => void run(`/platform/organizations/${o.id}/${kind}`, { reason })}>
      <div className="pf-dialog">
        <p className="pf-small">{t(suspend ? "pfSuspendHint" : "pfReactivateHint")}</p>
        {suspend && <label className="check"><input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} /> <span className="bad-text">{t("bdSuspendConfirm")}</span></label>}
        <Reason v={reason} set={setReason} />
      </div>
    </Modal>
  );
}

function RecoveryModal({ o, onClose, onDone }: { o: PlatformOrgDetail; onClose(): void; onDone: Done }) {
  const { t } = useLang();
  const [userId, setUserId] = useState(o.administrators[0]!.id);
  const [channel, setChannel] = useState<"sms" | "email">("sms");
  const [reason, setReason] = useState("");
  const { busy, alert, run } = useRun(onDone);
  return (
    <Modal title={t("pfRecovery")} onClose={onClose} busy={busy} alert={alert} saveLabel={t("pfStartRecovery")} testId="pf-recovery-dialog"
      saveDisabled={reason.trim().length < 5} onSubmit={() => void run(`/platform/organizations/${o.id}/recovery`, { userId, channel, reason })}>
      <div className="pf-dialog">
        <p className="pf-small">{t("pfRecoveryHint")}</p>
        <div className="pf-dates">
          <Field label={t("pfRecoveryFor")}><select className="control" value={userId} onChange={(e) => setUserId(e.target.value)}>{o.administrators.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.mobile}</option>)}</select></Field>
          <Field label={t("pfChannel")}><select className="control" value={channel} onChange={(e) => setChannel(e.target.value as "sms" | "email")}>
            <option value="sms">{t("pfChannel_sms")}</option>{o.signupDetail && <option value="email">{t("pfChannel_email")}</option>}</select></Field>
        </div>
        <Reason v={reason} set={setReason} />
      </div>
    </Modal>
  );
}

/** Assigns the business to a reseller, transfers it, or takes it back to Delta (migration 048). */
function ResellerModal({ o, onClose, onDone }: { o: PlatformOrgDetail; onClose(): void; onDone: Done }) {
  const { t, L } = useLang();
  const [list, setList] = useState<ResellerRowDto[]>([]);
  const [resellerId, setResellerId] = useState("");
  const [reason, setReason] = useState("");
  const [sure, setSure] = useState(false);
  const { busy, alert, run } = useRun(onDone);
  useEffect(() => { api<ResellerRowDto[]>("GET", "/platform/resellers").then((x) => setList(x.filter((r) => r.status === "active" && r.id !== o.reseller?.id)), () => undefined); }, [o.reseller?.id]);
  const transfer = !!o.reseller && resellerId !== "" && resellerId !== "delta";
  return (
    <Modal title={t("bdMore_reseller")} sub={o.reseller ? t("bdResellerNow", { x: L(o.reseller) }) : t("bdResellerNone")} onClose={onClose} busy={busy} alert={alert} saveLabel={t("bdMore_reseller")} testId="pf-reseller-dialog"
      saveDisabled={!resellerId || reason.trim().length < 5 || (transfer && !sure)}
      onSubmit={() => void run(`/platform/organizations/${o.id}/reseller`, { resellerId: resellerId === "delta" ? null : resellerId, reason, confirmTransfer: transfer })}>
      <div className="pf-dialog">
        <p className="pf-small">{t("bdResellerHint")}</p>
        <Field label={t("bdReseller")} req><select className="control" data-testid="pf-reseller-pick" value={resellerId} onChange={(e) => { setResellerId(e.target.value); setSure(false); }}>
          <option value="">{t("rsChoose")}</option>
          {o.reseller && <option value="delta">{t("bdResellerBack")}</option>}
          {list.map((r) => <option key={r.id} value={r.id}>{L(r)} ({r.code})</option>)}</select></Field>
        {transfer && <label className="check"><input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} /> <span className="bad-text">{t("rsConfirmTransfer")}</span></label>}
        <Reason v={reason} set={setReason} />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- activity

const obj = (x: unknown): Record<string, any> => (x && typeof x === "object" ? x as Record<string, any> : {});

/** One audit row as a sentence in the screen's language; the action's code is kept under Details. */
export function activityText(t: T, r: PlatformAuditDto, planName: (code: unknown) => string): string {
  const a = obj(r.after), b = obj(r.before), m = obj(r.meta);
  const key = `bdA_${r.action.replace(/\./g, "_")}`;
  const vars: Record<string, string | number> = {
    p: planName(a.plan ?? a.code ?? m.plan), s: a.startsOn ?? "", e: a.endsOn ?? "", b: b.endsOn ?? "",
    m: m.method ? t(`pfMethod_${m.method}`) : "", n: m.sessions ?? 0, c: String(a.code ?? m.plan ?? r.entityId ?? ""),
    r: [a.reference, a.amount && `${a.amount} ${a.currency ?? ""}`.trim()].filter(Boolean).join(" · ")
  };
  const s = t(key, vars);
  return s === key ? r.action : s;
}

export function ActivityList({ rows, plans, showBusiness }: { rows: PlatformAuditDto[]; plans: PlanDto[]; showBusiness?: boolean }) {
  const { t, L, lang } = useLang();
  const planName = (code: unknown) => { const p = plans.find((x) => x.code === code); return p ? L(p) : String(code ?? ""); };
  if (!rows.length) return <div className="state-box"><p>{t("bdNoActivity")}</p></div>;
  const json = (x: unknown) => (x === null || x === undefined ? null : JSON.stringify(x, null, 2));
  return (
    <ol className="pf-activity" data-testid="pf-audit">
      {rows.map((r) => {
        const parts: [string, string | null][] = [["bdBefore", json(r.before)], ["bdAfter", json(r.after)], ["bdMeta", json(r.meta)]];
        return (
          <li key={r.id} data-action={r.action}>
            <div className="pf-act-line"><b>{activityText(t, r, planName)}</b>
              {showBusiness && r.organizationId && <> · <Link to={`/platform/businesses/${r.organizationId}`}>{r.organizationName}</Link></>}</div>
            <div className="pf-small">{r.actor ?? t("pfSystem")} · <span className="num">{when(r.at, lang)}</span></div>
            {r.reason && <div className="pf-act-reason">{t("pfReason")}: {r.reason}</div>}
            <details className="pf-act-details"><summary>{t("bdDetails")}</summary>
              <dl className="pf-kv"><dt>{t("bdCode")}</dt><dd><code dir="ltr">{r.action}</code></dd>
                {parts.filter(([, v]) => v).map(([k, v]) => <Detail key={k} label={t(k)}><pre dir="ltr" className="pf-meta">{v}</pre></Detail>)}</dl>
            </details>
          </li>
        );
      })}
    </ol>
  );
}

const Detail = ({ label, children }: { label: string; children: ReactNode }) => <><dt>{label}</dt><dd>{children}</dd></>;
